import { t } from "@/languages/i18n"
import { message } from "@/lib/config/lib/antd-global"
import { TagAttributes } from "@/types/types"
import { generateDuplicateName } from "@/utils/duplicate-name-helper"
import { AxiosRequestConfig } from "axios"
import { HttpClient } from "../../../../app/api/HttpClient"
import { FilterSearchParams } from "../../../../lib/external-components"

export const API_PREFIX_PATH = "/api/tags"

export interface FetchTagsQueryConfig {
  fields?: string[]
  populate?: string | Record<string, any> | null
}

const sanitizeTagPayload = <T extends Record<string, any>>(payload: T): Partial<T> => {
  const sanitizedEntries = Object.entries(payload).filter(([, value]) => {
    if (value === null || value === undefined || value === "") return false
    if (Array.isArray(value) && value.length === 0) return false
    return true
  })

  return Object.fromEntries(sanitizedEntries) as Partial<T>
}

export const fetchTags = async (
  searchTerms: { [key: string]: string } = {},
  queryConfig: FetchTagsQueryConfig = {},
) => {
  const params: FilterSearchParams = {
    sort: "createdAt:desc",
    publicationState: "preview",
    filters: {},
    pagination: {
      pageSize: "20",
    },
    locale: "all",
  }

  if (queryConfig.fields?.length) {
    ; (params as any).fields = queryConfig.fields
  }

  if (queryConfig.populate !== undefined) {
    if (queryConfig.populate === null) {
      delete params.populate
    } else {
      params.populate = queryConfig.populate as any
    }
  }

  const excludedKeys = ["current", "pageSize", "start", "limit", "total", "page"]

  Object.keys(searchTerms).forEach(key => {
    const value = searchTerms[key]

    if (key === "sort") {
      params.sort = value
    } else if (key === "populate") {
      params.populate = value
    } else if (key === "locale") {
      params.locale = value
    } else if (key === "publicationState") {
      const convertParamStatusTag = value === "draft" || value === "all" ? "preview" : "live"
      params.publicationState = convertParamStatusTag

      if (value === "draft") {
        params.filters.publishedAt = { $null: true }
      } else {
        delete params.filters.publishedAt
      }
    } else if (!excludedKeys.includes(key)) {
      if (value) {
        if (key === "domain") {
          params.filters.domain = { $containsi: value }
        } else if (key === "name") {
          params.filters.$or = [
            { name: { $containsi: value } },
            { slug: { $containsi: value } },
            { description: { $containsi: value } },
          ]
        } else {
          params.filters[key] = { $containsi: value }
        }
      }
    } else if (value) {
      params.pagination = {
        ...params.pagination,
        [key]: value,
      }
    }
  })

  try {
    const response = await HttpClient.get(`${API_PREFIX_PATH}`, { params })
    return response
  } catch (error) {
    message.error(t("Không thể lấy danh mục. Vui lòng thử lại."))
  }
}

export const updateTag = async (id: string, updatedData: TagAttributes) => {
  return await HttpClient.put(`${API_PREFIX_PATH}/${id}`, { data: (updatedData) })
}
export const createTag = async (data: TagAttributes) => {
  return await HttpClient.post(`${API_PREFIX_PATH}`, { data: (data) })
}

export const deleteTag = async (id: number) => {
  return await HttpClient.delete(`${API_PREFIX_PATH}/${id}`)
}

export const getTagById = async (id: number, queryConfig: FetchTagsQueryConfig = {}) => {
  const params: Record<string, any> = {
    publicationState: "preview",
  }

  if (queryConfig.fields?.length) {
    params.fields = queryConfig.fields
  }

  if (queryConfig.populate !== undefined) {
    if (queryConfig.populate === null) {
      delete params.populate
    } else {
      params.populate = queryConfig.populate
    }
  }

  const response = await HttpClient.get(`${API_PREFIX_PATH}/${id}`, {
    params,
  })
  return response.data
}

export const checkSlug = async (slug: string, locale?: string) => {
  const params: AxiosRequestConfig["params"] = {
    filters: {
      slug: { $eq: slug },
    },
    publicationState: "preview",
  }
  if (locale) {
    params.locale = locale
  }
  return await HttpClient.get(`${API_PREFIX_PATH}`, { params })
}

const generateDuplicateSlug = (originalSlug: string, existingSlugs: string[]) => {
  const baseSlug = originalSlug.replace(/-copy-\d+$/i, "")
  let nextIndex = 1
  let nextSlug = `${baseSlug}-copy-${nextIndex}`

  while (existingSlugs.includes(nextSlug)) {
    nextIndex += 1
    nextSlug = `${baseSlug}-copy-${nextIndex}`
  }

  return nextSlug
}

export const duplicateTag = async (id: number) => {
  const originalTag = await getTagById(id)
  const originalAttributes = originalTag?.attributes || originalTag

  if (!originalAttributes?.name) {
    throw new Error("Invalid tag data")
  }

  const existingTagsResponse = await fetchTags({
    pageSize: "100",
    locale: originalAttributes.locale || "all",
    publicationState: "all",
  })

  const existingTags = Array.isArray(existingTagsResponse?.data) ? existingTagsResponse.data : []
  const existingNames = existingTags
    .map((item: any) => item?.attributes?.name)
    .filter((name: string | undefined): name is string => Boolean(name))
  const existingSlugs = existingTags
    .map((item: any) => item?.attributes?.slug)
    .filter((slug: string | undefined): slug is string => Boolean(slug))

  const duplicateData: TagAttributes & {
    posts?: number[]
    publishedAt?: string | null
  } = {
    name: generateDuplicateName(originalAttributes.name, existingNames),
    slug: generateDuplicateSlug(originalAttributes.slug || originalAttributes.name, existingSlugs),
    description: originalAttributes.description || "",
    domain: originalAttributes.domain || "",
    locale: originalAttributes.locale || "vi",
    posts:
      originalAttributes.posts?.data?.map((post: { id: number }) => post.id).filter((postId: number) => Boolean(postId)) ||
      [],
    publishedAt: null,
  }

  return await createTag(sanitizeTagPayload(duplicateData) as TagAttributes)
}
